/**
 * @param {object} rowData
 * @param {string} poType
 * @returns {{ sku: string | number, itemName: string, unitsPerCase: number | null }}
 */
function getLineItemDisplayFields(rowData, poType) {
    const original = rowData?.original ?? {};

    if (poType === 'PACKAGING') {
        const unitsPerCase = original.receivedUnitsPerCase ?? original.unitsPerCase ?? null;
        return {
            sku: original.masterSku,
            itemName: original.description ?? '',
            unitsPerCase: unitsPerCase != null ? Number(unitsPerCase) : null,
        };
    }

    const unitsPerCase = original.receivedUnitCount ?? original.baseUnitsPerCase ?? null;
    return {
        sku: original.itemMasterSKU,
        itemName: original.itemMasterName ?? '',
        unitsPerCase: unitsPerCase != null ? Number(unitsPerCase) : null,
    };
}

/**
 * @param {{ totalCases: number, numPallets: number, unitsPerCase?: number | null }} params
 * @returns {string}
 */
function formatReceivedSummary({ numPallets, totalCases, unitsPerCase }) {
    const segments = [
        `Total received: ${totalCases} ${totalCases === 1 ? 'case' : 'cases'}`,
        `${numPallets} ${numPallets === 1 ? 'pallet' : 'pallets'}`,
    ];

    if (unitsPerCase != null && !Number.isNaN(unitsPerCase)) {
        segments.push(`${unitsPerCase} units/case`);
    }

    return segments.join(' · ');
}

const DEFAULT_TAGS_PER_PALLET = 4;
const MAX_TAGS_PER_PALLET = 16;

/**
 * @param {string} rawValue
 * @returns {number | ''}
 */
function sanitizeTagsPerPalletInput(rawValue) {
    if (rawValue === '') {
        return '';
    }

    const value = parseInt(rawValue, 10);

    if (Number.isNaN(value) || value <= 0) {
        return '';
    }

    if (value > MAX_TAGS_PER_PALLET) {
        return MAX_TAGS_PER_PALLET;
    }

    return value;
}

/**
 * @param {number | string | ''} value
 * @returns {boolean}
 */
function isValidTagsPerPallet(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= MAX_TAGS_PER_PALLET;
}

/**
 * @param {number} numPallets
 * @returns {string}
 */
function formatTotalPalletsLabel(numPallets) {
    const count = Math.max(0, Math.floor(Number(numPallets) || 0));
    const palletLabel = count === 1 ? 'pallet' : 'pallets';
    return `${count} total ${palletLabel}`;
}

/**
 * @param {number | null | undefined} companySourceId
 * @param {Array<{ id: number, code: string, displayName: string }>} companySourceOptions
 * @returns {{ id: number, code: string, displayName: string } | null}
 */
function resolveCompanySource(companySourceId, companySourceOptions) {
    if (companySourceId == null) {
        return null;
    }

    return companySourceOptions.find((source) => source.id === companySourceId) ?? null;
}

/**
 * @param {string} rawValue
 * @param {number} maxCases
 * @returns {number | ''}
 */
function sanitizeCaseLabelCountInput(rawValue, maxCases) {
    if (rawValue === '') {
        return '';
    }

    const value = parseInt(rawValue, 10);

    if (Number.isNaN(value) || value <= 0) {
        return '';
    }

    const cap = Math.max(0, Math.floor(Number(maxCases) || 0));
    if (cap === 0) {
        return '';
    }

    if (value > cap) {
        return cap;
    }

    return value;
}

/**
 * @param {number | string | ''} value
 * @param {number} maxCases
 * @returns {boolean}
 */
function isValidCaseLabelCount(value, maxCases) {
    const cap = Math.max(0, Math.floor(Number(maxCases) || 0));
    if (cap === 0) {
        return false;
    }

    return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= cap;
}

export {
    DEFAULT_TAGS_PER_PALLET,
    MAX_TAGS_PER_PALLET,
    formatReceivedSummary,
    formatTotalPalletsLabel,
    getLineItemDisplayFields,
    isValidCaseLabelCount,
    isValidTagsPerPallet,
    resolveCompanySource,
    sanitizeCaseLabelCountInput,
    sanitizeTagsPerPalletInput,
};
